import { Extension, type Editor, type Range } from "@tiptap/core";
import { PluginKey, type EditorState } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { exitSuggestion, type SuggestionOptions, type SuggestionProps } from "@tiptap/suggestion";
import tippy, { type Instance } from "tippy.js";
import { SlashCommandMenu, type SlashCommandItem, type SlashCommandMenuRef } from "./slash-command-menu";

export type SlashCommandOptions = {
  heading: string;
  items: (args: { query: string }) => SlashCommandItem[] | Promise<SlashCommandItem[]>;
  onSelect: (args: { editor: Editor; range: Range; item: SlashCommandItem }) => void;
  emptyMessage?: () => string;
};

const slashCommandPluginKey = new PluginKey("slashCommands");

export function isSlashCommandActive(state: EditorState) {
  // Read the installed plugin; hot reload can replace the module's PluginKey while the editor survives.
  return state.plugins.some(
    (plugin) => plugin.spec.quakeSlashCommand === true && plugin.getState(state)?.active === true,
  );
}

export const findSlashCommandMatch: NonNullable<SuggestionOptions["findSuggestionMatch"]> = ({ $position }) => {
  const before = $position.parent.textBetween(0, $position.parentOffset, undefined, "\ufffc");
  const match = /(?:^|[\s([{])\/([^/\n[\]]{0,80})$/.exec(before);
  if (!match || /\s$/.test(match[1]!)) return null;
  const query = match[1]!;
  return { range: { from: $position.pos - query.length - 1, to: $position.pos }, query, text: `/${query}` };
};

/**
 * Escape keeps a menu dismissed while its "/" stays put. A "/" this transaction inserted is a new trigger, even at the
 * same position (select-all then "/", or a clear and retype that the DOM observer reads as one change).
 */
const insertsSlashTrigger: NonNullable<SuggestionOptions["shouldResetDismissed"]> = ({ transaction, range }) =>
  transaction.mapping.maps.some((map, index) => {
    const later = transaction.mapping.slice(index + 1);
    let inserted = false;
    map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
      if (later.map(newStart, -1) <= range.from && later.map(newEnd, 1) > range.from) inserted = true;
    });
    return inserted;
  });

/** Read options at invocation time so async catalogs and form callbacks never go stale. */
export function createSlashCommandExtension(getOptions: () => SlashCommandOptions | undefined) {
  return Extension.create({
    name: "slashCommands",
    onBlur() {
      exitSuggestion(this.editor.view, slashCommandPluginKey);
    },
    addProseMirrorPlugins() {
      const plugin = Suggestion<SlashCommandItem>({
        editor: this.editor,
        pluginKey: slashCommandPluginKey,
        char: "/",
        allowSpaces: true,
        findSuggestionMatch: findSlashCommandMatch,
        shouldResetDismissed: insertsSlashTrigger,
        allow: ({ editor }) => editor.isEditable,
        items: ({ query }) => getOptions()?.items({ query }) ?? [],
        command: ({ editor, range, props }) => {
          getOptions()?.onSelect({ editor, range, item: props });
        },
        render: () => {
          let component: ReactRenderer<SlashCommandMenuRef> | undefined;
          let popup: Instance | undefined;
          const update = (props: SuggestionProps<SlashCommandItem>) => {
            const options = getOptions();
            const menuProps = {
              ...props,
              heading: options?.heading ?? "Commands",
              emptyMessage: options?.emptyMessage?.() ?? "No matching commands.",
              isLoading: props.loading,
            };
            if (!component) {
              component = new ReactRenderer(SlashCommandMenu, { editor: props.editor, props: menuProps });
            } else {
              component.updateProps(menuProps);
            }
            const rect = props.clientRect?.();
            if (!rect) return;
            if (!popup) {
              popup = tippy(document.body, {
                getReferenceClientRect: () => rect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "top-start",
                arrow: false,
                maxWidth: "none",
                onClickOutside: () => exitSuggestion(props.editor.view, slashCommandPluginKey),
              });
            } else {
              popup.setProps({ getReferenceClientRect: () => props.clientRect?.() ?? rect });
            }
          };
          return {
            onBeforeStart: update,
            onBeforeUpdate: update,
            onStart: update,
            onUpdate: update,
            onKeyDown: (props) => {
              if (component?.ref) return component.ref.onKeyDown(props);
              if (
                !props.event.shiftKey &&
                !props.event.isComposing &&
                ["Enter", "ArrowUp", "ArrowDown"].includes(props.event.key)
              ) {
                props.event.preventDefault();
                return true;
              }
              return false;
            },
            onExit: () => {
              popup?.destroy();
              component?.destroy();
              popup = undefined;
              component = undefined;
            },
          };
        },
      });
      plugin.spec.quakeSlashCommand = true;
      return [plugin];
    },
  });
}
